import { useEffect, useRef, useState } from "react";

/**
 * Whether the element is on screen or within `margin` of it. For motion that
 * only matters while someone can see it: slideshows, rotating reviews,
 * marquees and pinging dots. Off-screen, Chrome cannot hand a looping
 * animation to the compositor and ticks it on the main thread in every frame;
 * pausing the off-screen loops cut an idle phone on the home page from about
 * 90 % to under 40 % main-thread load (390 px, 4x CPU throttle, 17.10.2026).
 * Starts true, so server and first client render agree.
 */
export function useInView<T extends Element>(margin = "200px 0px") {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: margin,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [margin]);
  return [ref, inView] as const;
}
